import { Container, Grid, Typography, Paper, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import BlueButton from '../../components/blueButton';
import RedButton from '../../components/redButton';
import globals from '../../globals';
import { useAuth } from '../../context/AuthContext';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';

const getTimeLeft = (target) => {
    if (!target) return null;
    const targetDate = new Date(target);
    if (Number.isNaN(targetDate.getTime())) return null;
    const difference = targetDate.getTime() - new Date().getTime();
    if (difference <= 0) return { isPast: true, days: 0, hours: 0, minutes: 0 };

    const totalMinutes = Math.floor(difference / (1000 * 60));
    return {
        isPast: false,
        days: Math.floor(totalMinutes / (60 * 24)),
        hours: Math.floor((totalMinutes % (60 * 24)) / 60),
        minutes: totalMinutes % 60,
    };
};

const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'long' });
};

const formatTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toTimeString().slice(0, 5);
};

// The rich-text editor saves markup like "<p></p>" for an empty field, so check for real text.
const hasText = (html) =>
    Boolean(html) && (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').trim() !== '';

const sectionTitleSx = { fontSize: 19, color: globals.colors.lightRed, fontWeight: 'bold', textAlign: 'left' };

// Section title and text with an optional image on the right. The image is height-capped and the
// title + text are vertically centred against it, so short text doesn't sit at the top next to a tall picture.
const TextWithImage = ({ title, html, imageUrl, alt }) => (
    <Grid container spacing={3} alignItems={'center'}>
        <Grid item xs={12} sm={imageUrl ? 8 : 12}>
            {title && <Typography sx={{ ...sectionTitleSx, mb: 1 }}>{title}</Typography>}
            <Typography component="div" sx={{ fontSize: 16 }} dangerouslySetInnerHTML={{ __html: html }} />
        </Grid>
        {imageUrl && (
            <Grid item xs={12} sm={4} textAlign={'center'}>
                <img
                    src={imageUrl}
                    alt={alt}
                    style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 8 }}
                />
            </Grid>
        )}
    </Grid>
);

// A rich-text section rendered inside a Paper card, optionally with an image beside it.
// Sections with no text written in the admin panel are not shown.
const RichSection = ({ title, html, imageUrl }) => {
    if (!hasText(html)) return null;
    return (
        <Paper sx={{ borderRadius: 2, mt: 3, py: 2, px: 3 }} elevation={4}>
            <TextWithImage title={title} html={html} imageUrl={imageUrl} alt={title} />
        </Paper>
    );
};

const infoLabelSx = { fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 17 };
const infoValueSx = { fontWeight: 'bold', color: globals.colors.lightRed, fontSize: 20, overflowWrap: 'anywhere', lineHeight: 1.3 };

const BIO_EXCERPT_LENGTH = 100;

// Plain-text excerpt of a rich-text bio, cut at a word boundary.
const bioExcerpt = (html) => {
    if (!html) return '';
    const text = (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
    if (text.length <= BIO_EXCERPT_LENGTH) return text;
    const cut = text.slice(0, BIO_EXCERPT_LENGTH);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[,.;:!?-]+$/, '')}…`;
};

// Short card for a lecturer who is an eAcademiaBg instructor: photo, name and the start of their bio.
const InstructorCard = ({ instructor, linkable }) => {
    const profileUrl = `/instructor/${instructor.id}`;
    const excerpt = bioExcerpt(instructor.description || instructor.education);
    const nameSx = { fontSize: 19, fontWeight: 'bold', color: globals.colors.darkBlue, textDecoration: 'none' };
    return (
        <Grid container spacing={2} alignItems={'center'} sx={{ mt: 0.5, mb: 1.5 }}>
            <Grid item xs={12} sm={'auto'} textAlign={'center'}>
                <img
                    src={instructor.image_url || '/noone.webp'}
                    alt={instructor.name}
                    style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 10 }}
                />
            </Grid>
            <Grid item xs={12} sm>
                {linkable
                    ? <Typography component={RouterLink} to={profileUrl} sx={{ ...nameSx, '&:hover': { color: globals.colors.lightRed } }}>{instructor.name}</Typography>
                    : <Typography sx={nameSx}>{instructor.name}</Typography>}
                {excerpt && <Typography sx={{ fontSize: 16, mt: 0.5 }}>{excerpt}</Typography>}
                {linkable && (
                    <Typography
                        component={RouterLink} to={profileUrl}
                        sx={{ display: 'inline-block', mt: 0.5, fontWeight: 'bold', color: globals.colors.lightRed, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Виж профила
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
};

// "Who is the lecturer?" — instructor profiles first, then any custom text for an external lecturer.
const LecturerSection = ({ instructors, html: rawHtml, imageUrl, linkable }) => {
    const html = hasText(rawHtml) ? rawHtml : null;
    if (!instructors.length) {
        return <RichSection title="Кой е лекторът?" html={html} imageUrl={imageUrl} />;
    }
    return (
        <Paper sx={{ borderRadius: 2, mt: 3, py: 2, px: 3 }} elevation={4}>
            <Typography sx={sectionTitleSx}>
                {instructors.length > 1 || html ? 'Кои са лекторите?' : 'Кой е лекторът?'}
            </Typography>
            {instructors.map((instructor) => (
                <InstructorCard key={instructor.id} instructor={instructor} linkable={linkable} />
            ))}
            {html && <TextWithImage html={html} imageUrl={imageUrl} alt="Лектор" />}
        </Paper>
    );
};

const WebinarPage = ({ webinar: webinarProp }) => {
    const isPreview = Boolean(webinarProp);
    const { slug } = useParams();
    const { user, token } = useAuth();
    const navigate = useNavigate();
    const [webinar, setWebinar] = useState(webinarProp || null);
    const [loading, setLoading] = useState(!webinarProp);
    const [notFound, setNotFound] = useState(false);
    const [timeLeft, setTimeLeft] = useState(getTimeLeft(webinarProp?.date));
    const [registered, setRegistered] = useState(Boolean(webinarProp?.is_registered));
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    // Preview mode: parent passes the webinar object directly.
    useEffect(() => {
        if (webinarProp) {
            setWebinar(webinarProp);
            setLoading(false);
        }
    }, [webinarProp]);

    // Public mode: fetch by slug.
    useEffect(() => {
        if (webinarProp || !slug) return;
        setLoading(true);
        // Send the token (when logged in) so the backend can tell us if this user is already registered.
        fetch(`${globals.apiUrl}/webinars/by-slug/${slug}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        })
            .then((res) => {
                if (!res.ok) throw new Error('not found');
                return res.json();
            })
            .then((data) => {
                setWebinar(data);
                setRegistered(Boolean(data.is_registered));
                setLoading(false);
            })
            .catch(() => {
                setNotFound(true);
                setLoading(false);
            });
    }, [slug, webinarProp, token]);

    useEffect(() => {
        setTimeLeft(getTimeLeft(webinar?.date));
        const interval = setInterval(() => setTimeLeft(getTimeLeft(webinar?.date)), 1000 * 60);
        return () => clearInterval(interval);
    }, [webinar?.date]);

    if (loading) {
        return (
            <Container sx={{ textAlign: 'center', mt: 10 }}>
                <CircularProgress sx={{ color: globals.colors.lightRed }} />
            </Container>
        );
    }

    if (notFound || !webinar) {
        return (
            <Container sx={{ textAlign: 'center', mt: 10, mb: 10 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 'bold', color: globals.colors.darkBlue }}>Уебинарът не е намерен.</Typography>
            </Container>
        );
    }

    const dateText = formatDate(webinar.date);
    const timeText = formatTime(webinar.date);
    const instructors = webinar.instructors || [];
    const lecturerNames = [...instructors.map((i) => i.name), webinar.lecturer_name].filter(Boolean);
    // Only a single lecturer is shown next to the date and time; several are listed in the lecturer section.
    const lecturerName = lecturerNames.length === 1 ? lecturerNames[0] : null;
    const headerImage = webinar.banner_image_url || webinar.cover_image_url || 'webinar.png';
    const isFree = webinar.price == null || Number(webinar.price) === 0;

    const handleCheckout = () => {
        if (isPreview) return;
        if (!user) {
            navigate(`/login?redirect=${encodeURIComponent(`/webinar/${slug}`)}`);
            return;
        }
        setCheckoutLoading(true);
        fetch(`${globals.apiUrl}/webinars/${webinar.id}/create-checkout`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.detail === 'You are already registered for this webinar') {
                    setRegistered(true);
                    setCheckoutLoading(false);
                } else if (data.url) {
                    window.location.href = data.url;
                } else if (data.free) {
                    setRegistered(true);
                    setCheckoutLoading(false);
                } else {
                    setCheckoutLoading(false);
                }
            })
            .catch(() => setCheckoutLoading(false));
    };

    let buttonText = isFree ? 'Запиши се безплатно' : `Добави в количката${webinar.price != null ? `  €${webinar.price}` : ''}`;
    if (registered) buttonText = 'Вече си записан';
    if (checkoutLoading) buttonText = 'Зареждане...';

    const priceButton = (
        <Grid container direction={'column'} alignItems={'center'} textAlign={'center'} sx={{ mt: 5, mb: 5 }}>
            <Grid item>
                <RedButton
                    text={buttonText}
                    size={'large'}
                    pX={6}
                    sx={{ fontSize: 19, py: 1.2 }}
                    startIcon={registered ? null : <AddShoppingCartIcon />}
                    disabled={registered || checkoutLoading}
                    onClick={registered ? undefined : handleCheckout}
                />
            </Grid>
            {!registered && (
                <Grid item sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 13, color: globals.colors.gray }}>
                        Нужна е регистрация за записване в уебинара
                    </Typography>
                </Grid>
            )}
        </Grid>
    );

    return (
        <Container maxWidth='lg'>
            <Helmet>
                {webinar.meta_title && <title>{webinar.meta_title}</title>}
                {webinar.meta_description && <meta name="description" content={webinar.meta_description} />}
                {webinar.meta_title && <meta property="og:title" content={webinar.meta_title} />}
                {webinar.meta_description && <meta property="og:description" content={webinar.meta_description} />}
            </Helmet>

            <Grid container justifyContent={'center'} textAlign={'center'} spacing={2} direction={'column'}>
                <Grid item>
                    <img src={headerImage} style={{ maxHeight: '25vw', maxWidth: '100%', width: 'auto' }} alt={webinar.title || 'Уебинар'} />
                </Grid>
                <Grid item>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 30, color: globals.colors.lightRed }}>{webinar.title}</Typography>
                </Grid>
                <Grid item>
                    <BlueButton text={isFree ? 'Безплатен уебинар' : 'Уебинар'} />
                </Grid>
            </Grid>

            {(dateText || timeText || lecturerName) && (
                <Paper sx={{ borderRadius: 2, mt: 5, py: 1, px: 5 }} elevation={4}>
                    <Grid container justifyContent={'space-around'} textAlign={'center'} spacing={3}>
                        {dateText && (
                            <Grid item>
                                <Typography sx={infoLabelSx}>Дата:</Typography>
                                <Typography sx={infoValueSx}>{dateText}</Typography>
                            </Grid>
                        )}
                        {timeText && (
                            <Grid item>
                                <Typography sx={infoLabelSx}>Час:</Typography>
                                <Typography sx={infoValueSx}>{timeText}</Typography>
                            </Grid>
                        )}
                        {lecturerName && (
                            // Width cap so a long name wraps instead of pushing the date and time aside.
                            <Grid item sx={{ maxWidth: { xs: '100%', sm: '40%' } }}>
                                <Typography sx={infoLabelSx}>Лектор:</Typography>
                                <Typography sx={infoValueSx}>{lecturerName}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            )}

            {timeLeft && (
                <Paper sx={{ borderRadius: 2, mt: 5, py: 1, px: 2 }} elevation={4}>
                    <Grid alignItems={'center'} textAlign={'center'}>
                        <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 20 }}>
                            <AccessTimeFilledIcon sx={{ mb: -0.5, mr: 1, color: globals.colors.lightRed }} />Остават:
                        </Typography>
                    </Grid>
                    <Grid container justifyContent={'center'} textAlign={'center'} sx={{ mt: 1 }}>
                        <Grid item xs={2}>
                            <Typography sx={{ fontWeight: 'bold', color: globals.colors.lightRed, fontSize: 25 }}>{timeLeft.days}</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 17 }}>Дни</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography sx={{ fontWeight: 'bold', color: globals.colors.lightRed, fontSize: 25 }}>{timeLeft.hours}</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 17 }}>Часа</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography sx={{ fontWeight: 'bold', color: globals.colors.lightRed, fontSize: 25 }}>{timeLeft.minutes}</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 17 }}>Минути</Typography>
                        </Grid>
                    </Grid>
                    {timeLeft.isPast && (
                        <Grid textAlign={'center'} sx={{ mt: 1 }}>
                            <Typography sx={{ fontWeight: 'bold', color: globals.colors.lightRed }}>Уебинарът вече се проведе.</Typography>
                        </Grid>
                    )}
                </Paper>
            )}

            {priceButton}

            <RichSection title="Какво представлява уебинарът?" html={webinar.what_is} />
            <RichSection title="Какво ще научиш?" html={webinar.what_learn} imageUrl={webinar.what_learn_image_url} />
            <RichSection title="За кого е подходящ?" html={webinar.suitable_for} />
            <LecturerSection instructors={instructors} html={webinar.lecturer} imageUrl={webinar.lecturer_image_url} linkable={!isPreview} />
            <RichSection title="Как ще се проведе?" html={webinar.how_conducted} />
            <RichSection title="Какво ще получиш?" html={webinar.what_get} />
            <RichSection title="Програма" html={webinar.program} />
            <RichSection title="Как да се запишеш?" html={webinar.how_to_enroll} imageUrl={webinar.how_to_enroll_image_url} />

            {priceButton}
        </Container>
    );
};

export default WebinarPage;
